import type { Application, Holder, KycCase } from '../../data/types';
import { addDays, date } from '../../lib/format';
import { KYC_TYPE_LABELS } from '../../lib/labels';
import { wholesaleStatus, type WholesaleMode } from './wholesale';

export type CheckState = 'OK' | 'PENDING' | 'ATTENTION' | 'NOT_NEEDED';

export interface PersonReadiness {
  id: string;
  name: string;
  role: string;
  identity: CheckState;
  pep: boolean;
  sourceOfWealth: CheckState;
  certificateExpiresAt?: string;
  hasCertificate: boolean;
}

export interface ReadinessIssue {
  text: string;
  blocking: boolean;
}

export interface Readiness {
  people: PersonReadiness[];
  issues: ReadinessIssue[];
  ready: boolean;
}

const OPEN_CASE = new Set(['OPEN', 'IN_REVIEW', 'ESCALATED']);
const BLOCKING_CASES = new Set([
  'PEP_MATCH',
  'SANCTIONS_POTENTIAL',
  'ID_MISMATCH',
  'UBO_INCOMPLETE',
]);

const identityState = (status: Holder['kycStatus']): CheckState =>
  status === 'VERIFIED' ? 'OK' : status === 'PENDING' ? 'PENDING' : 'ATTENTION';

export function peopleFor(holder: Holder, cases: KycCase[] = []): PersonReadiness[] {
  const needsWealth = (pep: boolean) => pep || holder.riskRating === 'HIGH';
  const pepNames = new Set(
    cases
      .filter(
        (item) =>
          item.holderId === holder.id && item.type === 'PEP_MATCH' && item.status !== 'CLEARED',
      )
      .map((item) => item.subjectName),
  );
  const owners = holder.beneficialOwners.filter((owner) => owner.role !== 'BENEFICIARY');
  if (owners.length === 0) {
    return [
      {
        id: holder.id,
        name: holder.contactName || holder.name,
        role: 'Investor',
        identity: identityState(holder.kycStatus),
        pep: holder.pep,
        hasCertificate: Boolean(holder.wholesale),
        certificateExpiresAt: holder.wholesale?.expiresAt,
        sourceOfWealth: needsWealth(holder.pep)
          ? holder.sourceOfWealthVerifiedAt
            ? 'OK'
            : 'ATTENTION'
          : 'NOT_NEEDED',
      },
    ];
  }
  return owners.map((owner) => {
    const pep = owner.pep || pepNames.has(owner.name);
    return {
      id: owner.id,
      name: owner.name,
      role: owner.role.charAt(0) + owner.role.slice(1).toLowerCase(),
      identity: identityState(owner.kycStatus),
      pep,
      hasCertificate: Boolean(owner.wholesale),
      certificateExpiresAt: owner.wholesale?.expiresAt,
      sourceOfWealth: needsWealth(pep)
        ? owner.sourceOfWealthVerifiedAt
          ? 'OK'
          : 'ATTENTION'
        : 'NOT_NEEDED',
    };
  });
}

function caseIssues(cases: KycCase[]): ReadinessIssue[] {
  return cases
    .filter((item) => OPEN_CASE.has(item.status))
    .map((item) => ({
      text: `${KYC_TYPE_LABELS[item.type]}: ${item.subjectName}`,
      blocking: BLOCKING_CASES.has(item.type),
    }));
}

function wholesaleIssues(
  holder: Holder,
  mode: WholesaleMode,
  asAt: string,
  settlementDate?: string,
): ReadinessIssue[] {
  const status = wholesaleStatus(holder, mode, asAt);
  if (!status.qualified) {
    if (status.manual) return [{ text: 'Wholesale status not yet set by staff', blocking: true }];
    return [
      { text: `No valid wholesale certificate: ${status.missing.join(', ')}`, blocking: true },
    ];
  }
  const expiresAt = status.governing?.expiresAt;
  if (!expiresAt) return [];
  const who = status.governing?.name ?? holder.name;
  if (settlementDate && expiresAt < settlementDate)
    return [
      {
        text: `Wholesale certificate for ${who} expires ${date(expiresAt)}, before settlement`,
        blocking: true,
      },
    ];
  if (expiresAt < addDays(asAt, 60))
    return [
      { text: `Wholesale certificate for ${who} expires ${date(expiresAt)}`, blocking: false },
    ];
  return [];
}

function finish(people: PersonReadiness[], issues: ReadinessIssue[]): Readiness {
  const sorted = [...issues].sort((left, right) => Number(right.blocking) - Number(left.blocking));
  return { people, issues: sorted, ready: !sorted.some((issue) => issue.blocking) };
}

export function holderReadiness(
  holder: Holder,
  cases: KycCase[],
  asAt: string,
  settlementDate?: string,
  mode: WholesaleMode = 'PER_INVESTOR_TYPE',
): Readiness {
  const people = peopleFor(holder, cases);
  const issues: ReadinessIssue[] = [];
  for (const person of people) {
    if (person.identity === 'ATTENTION')
      issues.push({ text: `Identity not verified: ${person.name}`, blocking: true });
    if (person.identity === 'PENDING')
      issues.push({ text: `Identity check in progress: ${person.name}`, blocking: false });
    if (person.sourceOfWealth === 'ATTENTION')
      issues.push({ text: `Source of wealth needed: ${person.name}`, blocking: true });
  }
  issues.push(
    ...wholesaleIssues(holder, mode, asAt, settlementDate),
    ...caseIssues(cases.filter((item) => item.holderId === holder.id)),
  );
  return finish(people, issues);
}

export function applicationReadiness(
  application: Application,
  holder: Holder | undefined,
  cases: KycCase[],
  asAt: string,
  settlementDate?: string,
  mode: WholesaleMode = 'PER_INVESTOR_TYPE',
): Readiness {
  const applicationCases = caseIssues(
    cases.filter((item) => item.applicationId === application.id),
  );
  if (holder) {
    const base = holderReadiness(holder, cases, asAt, settlementDate, mode);
    return finish(base.people, [...base.issues, ...applicationCases]);
  }
  const { checklist } = application;
  const issues: ReadinessIssue[] = [...applicationCases];
  if (checklist.identity === 'ISSUE')
    issues.push({ text: 'Identity check referred', blocking: true });
  if (checklist.identity === 'PENDING')
    issues.push({ text: 'Identity not yet verified', blocking: true });
  if (checklist.owners !== 'DONE')
    issues.push({ text: 'Beneficial owners incomplete', blocking: true });
  if (checklist.wholesale !== 'DONE')
    issues.push({ text: 'Wholesale certificate not provided', blocking: true });
  return finish(
    [
      {
        id: application.id,
        name: application.contactName,
        role: 'Applicant',
        identity:
          checklist.identity === 'DONE'
            ? 'OK'
            : checklist.identity === 'ISSUE'
              ? 'ATTENTION'
              : 'PENDING',
        pep: false,
        hasCertificate: checklist.wholesale === 'DONE',
        sourceOfWealth: 'NOT_NEEDED',
      },
    ],
    issues,
  );
}
